import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'utils/i18n';
import translations from './translations';
import './Strategy.css';

/**
 * Strategy Component
 * Displays company strategy information with Platform and Products tabs
 * Enhanced with professional visuals matching the homepage
 */
const Strategy = () => {
  const { language } = useTranslation();
  const [activeMainTab, setActiveMainTab] = useState('platform');
  const [activeProductTab, setActiveProductTab] = useState('bot');
  
  const heroCanvasRef = useRef(null);
  const platformCanvasRef = useRef(null);

  // Get page-specific translations
  const t = (key, defaultValue = '') => {
    const pageTranslations = translations[language] || translations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], pageTranslations);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    return defaultValue !== '' ? defaultValue : key;
  };

  // Animated hero canvas background
  useEffect(() => {
    const canvas = heroCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let time = 0;

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };

    const drawGrid = () => {
      const width = canvas.width / (window.devicePixelRatio || 1);
      const height = canvas.height / (window.devicePixelRatio || 1);
      
      ctx.clearRect(0, 0, width, height);
      
      // Subtle grid pattern
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.lineWidth = 1;
      
      const gridSize = 60;
      const offsetX = (time * 0.02) % gridSize;
      const offsetY = (time * 0.01) % gridSize;
      
      // Vertical lines
      for (let x = -gridSize + offsetX; x < width + gridSize; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      
      // Horizontal lines
      for (let y = -gridSize + offsetY; y < height + gridSize; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      
      // Floating dots at intersections
      ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
      for (let x = -gridSize + offsetX; x < width + gridSize; x += gridSize) {
        for (let y = -gridSize + offsetY; y < height + gridSize; y += gridSize) {
          const pulseSize = 2 + Math.sin(time * 0.003 + x * 0.01 + y * 0.01) * 1;
          ctx.beginPath();
          ctx.arc(x, y, pulseSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };

    const animate = () => {
      time += 1;
      drawGrid();
      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    animate();

    window.addEventListener('resize', resizeCanvas);
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  // Animated platform canvas background (wavy lines)
  useEffect(() => {
    const canvas = platformCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let time = 0;

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.parentElement.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);
    };

    const drawWavyLines = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      
      ctx.clearRect(0, 0, width, height);
      
      // Light blue color for wavy lines
      const strokeColor = 'rgba(88, 151, 222, 0.3)';
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';

      const numLines = 15;
      const density = 0.8;
      const curvature = -0.1;

      for (let i = 0; i <= numLines; i++) {
        const t = i / numLines;
        const startX = t * width;
        const startY = height;
        
        // Animated wave offset
        const waveOffset = Math.sin(time * 0.0008 + t * Math.PI * 2) * 30;
        const curveX = width * Math.pow(1 - t, 2) * curvature + waveOffset;
        const curveY = height * (1 - Math.pow(t, 2)) * curvature;
        const endX = curveX;
        const endY = startY - (1 - t) * startY + curveY;

        if (i % Math.round(1 / density) === 0) {
          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(endX, endY);
          ctx.stroke();
          
          // Reset stroke style for next line
          ctx.strokeStyle = strokeColor;
        }
      }
    };

    const animate = () => {
      time += 2;
      drawWavyLines();
      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    drawWavyLines();
    animate();

    const handleResize = () => {
      resizeCanvas();
      drawWavyLines();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const audiences = [
    {
      key: 'vendors',
      number: 1,
      title: t('platform.vendors.title', 'For SAAS Vendors'),
      content: t('platform.vendors.content', 'One-stop shop for hosting and managing SAAS company - accounting, payments, user management, SLA, marketing access. A one stop shop for saas vendors of any size to host and manage their company.'),
      colorClass: 'strategy-audience-card-blue'
    },
    {
      key: 'partners',
      number: 2,
      title: t('platform.partners.title', 'For Reseller / Implementation Partners'),
      content: t('platform.partners.content', 'Access to a wide variety of SAAS that they can implement and resell for customers with their own services attached such as maintenance, implementation and customization. Access to a healthy ecosystem of vendors and customers is crucial to that as well as out of the box CRM and accounting and payment management for anything happening on the B-esper platform for partners attempts to make this the go to platform for B2B SAAS.'),
      colorClass: 'strategy-audience-card-green'
    },
    {
      key: 'customers',
      number: 3,
      title: t('platform.customers.title', 'For Customers'),
      content: t('platform.customers.content', 'We aim to make a large and trusted ecosystem of SAAS that can be easily accessed, managed and is compliant with the highest standards of enterprise architecture for SAAS while being simple for anyone to understand and use.'),
      colorClass: 'strategy-audience-card-purple'
    }
  ];

  const products = [
    {
      key: 'bot',
      title: t('products.bot.title', 'B-esper Bot'),
      content: t('products.bot.content', 'Placeholder content for B-esper Bot strategy. This section will contain detailed information about the B-esper Bot product strategy, its goals, target market, and key features.'),
      badge: 'Flagship',
      badgeClass: 'flagship'
    },
    {
      key: 'labs',
      title: t('products.labs.title', 'B-esper Labs'),
      content: t('products.labs.content', 'B-esper Labs is an AI lab platform that allows hosting and using AI labs with compute and datasets across multiple cloud providers. Complete infrastructure and tools for AI research, development, and deployment.'),
      badge: 'Coming Soon',
      badgeClass: 'coming-soon'
    },
    {
      key: 'research',
      title: t('products.research.title', 'B-esper Research'),
      content: t('products.research.content', 'Placeholder content for B-esper Research strategy. This section will contain detailed information about the research product strategy, its goals, target market, and key features.'),
      badge: 'Coming Soon',
      badgeClass: 'coming-soon'
    },
    {
      key: 'operations',
      title: t('products.operations.title', 'B-esper Operations'),
      content: t('products.operations.content', 'Placeholder content for B-esper Operations strategy. This section will contain detailed information about the operations product strategy, its goals, target market, and key features.'),
      badge: 'Coming Soon',
      badgeClass: 'coming-soon'
    }
  ];

  return (
    <div className="strategy-page">
      {/* Hero Section */}
      <section className="strategy-hero-section">
        <canvas ref={heroCanvasRef} className="strategy-hero-canvas" aria-hidden="true" />
        <div className="strategy-hero-container">
          <div className="strategy-hero-content">
            <div className="strategy-hero-badge">
              <span className="strategy-hero-badge-dot"></span>
              {t('hero.badge', 'Our Strategy')}
            </div>
            <h1 className="strategy-hero-title">
              {t('title', 'Our Strategy')}
            </h1>
            <p className="strategy-hero-subtitle">
              {t('description', 'Our vision, mission, and strategic approach to delivering innovative AI solutions')}
            </p>
            
            {/* Professional Tab Toggle in Header */}
            <div className="strategy-hero-tabs">
              <button
                className={`strategy-hero-tab ${activeMainTab === 'platform' ? 'active' : ''}`}
                onClick={() => setActiveMainTab('platform')}
                type="button"
              >
                <div className="strategy-hero-tab-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" rx="1"/>
                    <rect x="14" y="3" width="7" height="7" rx="1"/>
                    <rect x="3" y="14" width="7" height="7" rx="1"/>
                    <rect x="14" y="14" width="7" height="7" rx="1"/>
                    <line x1="10" y1="10" x2="14" y2="10"/>
                    <line x1="10" y1="14" x2="14" y2="14"/>
                    <line x1="10" y1="10" x2="10" y2="14"/>
                    <line x1="14" y1="10" x2="14" y2="14"/>
                  </svg>
                </div>
                <div className="strategy-hero-tab-content">
                  <span className="strategy-hero-tab-label">{t('mainTabs.platform', 'Platform Strategy')}</span>
                  <span className="strategy-hero-tab-description">{t('hero.tabs.platform.desc', 'Infrastructure & Ecosystem')}</span>
                </div>
              </button>
              <button
                className={`strategy-hero-tab ${activeMainTab === 'products' ? 'active' : ''}`}
                onClick={() => setActiveMainTab('products')}
                type="button"
              >
                <div className="strategy-hero-tab-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                    <line x1="12" y1="22.08" x2="12" y2="12"/>
                    <path d="M12 12.01L3.27 6.96M12 12.01l8.73-5.05"/>
                  </svg>
                </div>
                <div className="strategy-hero-tab-content">
                  <span className="strategy-hero-tab-label">{t('mainTabs.products', 'Product Strategy')}</span>
                  <span className="strategy-hero-tab-description">{t('hero.tabs.products.desc', 'Solutions & Offerings')}</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Strategy Tab */}
      {activeMainTab === 'platform' && (
        <div className="strategy-tab-content">
          <section className="strategy-platform-section">
            <canvas 
              ref={platformCanvasRef}
              className="strategy-platform-canvas"
              aria-hidden="true"
            />
            <div className="strategy-platform-container">
              <div className="strategy-platform-header">
                <div className="strategy-platform-badge">
                  <span>{t('platform.audiences.title', 'Our Strategy by Audience')}</span>
                </div>
                <h2 className="strategy-platform-title">
                  {t('platform.audiences.title', 'Our Strategy by Audience')}
                </h2>
              </div>

              {/* Structured Mission Content */}
              <div className="strategy-platform-content">
                {/* Mission Section */}
                <div className="strategy-content-section">
                  <div className="strategy-content-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                      <path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <div className="strategy-content-text">
                    <h3 className="strategy-content-title">{t('platform.mission.title', 'Our Mission')}</h3>
                    <p className="strategy-content-description">{t('platform.mission.content', 'The advent of LLMs has dramatically reduced software development costs. B-esper\'s mission is to provide SaaS startups, established providers, and open-source projects with a comprehensive managed environment to host and scale their businesses.')}</p>
                  </div>
                </div>

                {/* Vision Section */}
                <div className="strategy-content-section">
                  <div className="strategy-content-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 6v6l4 2"/>
                    </svg>
                  </div>
                  <div className="strategy-content-text">
                    <h3 className="strategy-content-title">{t('platform.vision.title', 'Complete Infrastructure Solution')}</h3>
                    <p className="strategy-content-description">{t('platform.vision.content', 'A one-stop platform where everything is provided—from accounting and payment management to user security, SaaS hosting, SLAs, and access to marketing channels and B2B SaaS customers.')}</p>
                  </div>
                </div>

                {/* Features Grid */}
                <div className="strategy-features-grid">
                  <div className="strategy-feature-item">
                    <div className="strategy-feature-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="2" x2="12" y2="22"/>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                      </svg>
                    </div>
                    <h4 className="strategy-feature-title">{t('platform.features.items.0.title', 'Accounting & Finance')}</h4>
                    <p className="strategy-feature-desc">{t('platform.features.items.0.description', 'Complete financial management and reporting')}</p>
                  </div>
                  <div className="strategy-feature-item">
                    <div className="strategy-feature-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                        <line x1="1" y1="10" x2="23" y2="10"/>
                      </svg>
                    </div>
                    <h4 className="strategy-feature-title">{t('platform.features.items.1.title', 'Payment Processing')}</h4>
                    <p className="strategy-feature-desc">{t('platform.features.items.1.description', 'Seamless payment handling and billing')}</p>
                  </div>
                  <div className="strategy-feature-item">
                    <div className="strategy-feature-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                    </div>
                    <h4 className="strategy-feature-title">{t('platform.features.items.2.title', 'User & Security Management')}</h4>
                    <p className="strategy-feature-desc">{t('platform.features.items.2.description', 'Enterprise-grade security and access control')}</p>
                  </div>
                  <div className="strategy-feature-item">
                    <div className="strategy-feature-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                        <line x1="8" y1="21" x2="16" y2="21"/>
                        <line x1="12" y1="17" x2="12" y2="21"/>
                      </svg>
                    </div>
                    <h4 className="strategy-feature-title">{t('platform.features.items.3.title', 'SaaS Hosting & SLA')}</h4>
                    <p className="strategy-feature-desc">{t('platform.features.items.3.description', 'Reliable infrastructure with guaranteed uptime')}</p>
                  </div>
                  <div className="strategy-feature-item">
                    <div className="strategy-feature-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                    </div>
                    <h4 className="strategy-feature-title">{t('platform.features.items.4.title', 'Marketing & Discovery')}</h4>
                    <p className="strategy-feature-desc">{t('platform.features.items.4.description', 'Access to B2B SaaS marketplace and customers')}</p>
                  </div>
                  <div className="strategy-feature-item">
                    <div className="strategy-feature-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                        <line x1="12" y1="22.08" x2="12" y2="12"/>
                      </svg>
                    </div>
                    <h4 className="strategy-feature-title">{t('platform.features.items.5.title', 'Full Operations Suite')}</h4>
                    <p className="strategy-feature-desc">{t('platform.features.items.5.description', 'CRM, support, and complete business operations')}</p>
                  </div>
                </div>

                {/* Ecosystem Section */}
                <div className="strategy-content-section">
                  <div className="strategy-content-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 6v6l4 2"/>
                    </svg>
                  </div>
                  <div className="strategy-content-text">
                    <h3 className="strategy-content-title">{t('platform.ecosystem.title', 'Building the Ecosystem')}</h3>
                    <p className="strategy-content-description">{t('platform.ecosystem.content', 'To kickstart this ecosystem and demonstrate viability, B-esper is launching startups that fully operate on B-esper infrastructure—hosting both their codebase/containers and all operational services including payments, accounting, CRM, and more.')}</p>
                  </div>
                </div>

                {/* Goal Section */}
                <div className="strategy-goal-section">
                  <div className="strategy-goal-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>
                  <h3 className="strategy-goal-title">{t('platform.goal.title', 'Our Goal')}</h3>
                  <p className="strategy-goal-content">{t('platform.goal.content', 'Make SaaS easy to start and sell.')}</p>
                </div>
              </div>

              {/* Audience Cards */}
              <div className="strategy-audiences-grid">
                {audiences.map((audience) => (
                  <div key={audience.key} className={`strategy-audience-card ${audience.colorClass}`}>
                    <div className="strategy-audience-header">
                      <div className="strategy-audience-number">{audience.number}</div>
                      <h3 className="strategy-audience-title">{audience.title}</h3>
                    </div>
                    <p className="strategy-audience-content">{audience.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Products Strategy Tab */}
      {activeMainTab === 'products' && (
        <div className="strategy-tab-content">
          <section className="strategy-products-section">
            <div className="strategy-products-container">
              <div className="strategy-products-header">
                <h2 className="strategy-products-title">
                  {t('mainTabs.products', 'B-esper Products Strategy')}
                </h2>
                <p className="strategy-products-subtitle">
                  {t('products.subtitle', 'Explore our product portfolio and strategic roadmap')}
                </p>
              </div>

              {/* Interactive Product Cards Grid */}
              <div className="strategy-products-grid">
                {products.map((product) => (
                  <div
                    key={product.key}
                    className={`strategy-product-card ${activeProductTab === product.key ? 'active' : ''}`}
                    onClick={() => setActiveProductTab(product.key)}
                  >
                    <div className={`strategy-product-badge ${product.badgeClass}`}>
                      {product.badge}
                    </div>
                    <div className="strategy-product-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <path d="M9 9h6v6H9z"/>
                      </svg>
                    </div>
                    <div className="strategy-product-header">
                      <h3 className="strategy-product-card-title">{product.title}</h3>
                    </div>
                    <p className="strategy-product-content">{product.content}</p>
                    <div className="strategy-product-indicator">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default Strategy;
